import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { settingsService } from 'v2/shared/services/settings-service'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RSandboxAddPayload, SandboxOtpChannel } from 'v2/types/api/settings'

export const useSandboxNumbersQuery = (enabled = true) =>
  useQuery({
    queryKey: getSettingsQueryKeys().sandboxNumbers,
    enabled,
    queryFn: async () => {
      const response = await settingsService.getSandboxNumbers()
      return response.data
    },
  })

const useInvalidateSandboxNumbers = () => {
  const queryClient = useQueryClient()
  // Prefix-match on the base key so this keeps working when the domain gains
  // parameterised queries.
  return () =>
    queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().sandboxNumbersBase })
}

/**
 * Add doubles as a resend for a row that is still pending - number_service
 * upserts and issues a fresh session_uuid.
 *
 * On an already-verified number it short-circuits to 200 and sends no OTP. The
 * service layer hands back core's envelope rather than the axios response, so
 * the tell is the ABSENCE of session_uuid, not the status code.
 */
export const useAddSandboxNumberMutation = () => {
  const invalidate = useInvalidateSandboxNumbers()
  return useMutation({
    mutationFn: async (payload: RSandboxAddPayload) => {
      const response = await settingsService.addSandboxNumber(payload)
      return response.data
    },
    onSuccess: invalidate,
  })
}

export const useVerifySandboxNumberMutation = () => {
  const invalidate = useInvalidateSandboxNumbers()
  return useMutation({
    mutationFn: async ({ number, code }: { number: string; code: string }) => {
      const response = await settingsService.verifySandboxNumber(number, { code })
      return response.data
    },
    onSuccess: invalidate,
  })
}

/** Refused with 409 on an already-verified row - only offer this while pending. */
export const useResendSandboxOtpMutation = () => {
  const invalidate = useInvalidateSandboxNumbers()
  return useMutation({
    mutationFn: async ({ number, channel }: { number: string; channel?: SandboxOtpChannel }) => {
      const response = await settingsService.resendSandboxNumberOtp(number, { channel })
      return response.data
    },
    onSuccess: invalidate,
  })
}

/** Idempotent upstream - deleting a row that is already gone still returns 204. */
export const useDeleteSandboxNumberMutation = () => {
  const invalidate = useInvalidateSandboxNumbers()
  return useMutation({
    mutationFn: (number: string) => settingsService.deleteSandboxNumber(number),
    onSuccess: invalidate,
  })
}
